import { RAL_CLASSIC, type RalColor } from "@/features/onboarding/ralClassic";

/**
 * Unternehmensfarben: Der Account arbeitet nur mit Hex. RAL und CMYK sind
 * Eingabewege für Kunden, die ihre Farbe so kennen — daraus wird ein
 * ungefährer Hex-Wert, und die Angabe des Kunden bleibt als Herkunft daneben
 * stehen (`primaryRal` bzw. `primaryCmyk`, höchstens eins von beiden).
 */

/** Findet einen RAL-Classic-Ton über seine Nummer: „5015“, „RAL 5015“ oder ein gespeichertes „RAL 5015 Himmelblau“. */
export function findRalColor(value: string | undefined): RalColor | undefined {
  const code = value?.match(/(?:^|\D)(\d{4})(?:\D|$)/)?.[1];
  return code ? RAL_CLASSIC.find((color) => color.code === code) : undefined;
}

export const ralLabel = (color: RalColor) => `RAL ${color.code} ${color.name}`;

export type Cmyk = [number, number, number, number];

/** Ein CMYK-Anteil in ganzen Prozent, 0 bis 100; ein angehängtes „%“ ist erlaubt. */
export function parseCmykPart(raw: string): number | null {
  const value = raw.trim().replace(/\s*%$/, "");
  if (!/^\d{1,3}$/.test(value)) return null;
  const n = Number(value);
  return n <= 100 ? n : null;
}

/** Gespeicherte Form „100/60/0/0“ zurück in vier Zahlen; alles andere ergibt null. */
export function parseCmyk(value: string | undefined): Cmyk | null {
  const parts = (value ?? "").split("/");
  if (parts.length !== 4) return null;
  const numbers = parts.map(parseCmykPart);
  return numbers.every((n) => n !== null) ? (numbers as Cmyk) : null;
}

export const formatCmyk = (cmyk: Cmyk) => cmyk.join("/");

/**
 * Einfache Umrechnung ohne Farbprofil. Für Druckdaten taugt sie nicht, für
 * eine Bildschirmfarbe im Account reicht sie — deshalb der Hinweis „ungefähr“.
 */
export function cmykToHex([c, m, y, k]: Cmyk): string {
  const channel = (part: number) => Math.round(255 * (1 - part / 100) * (1 - k / 100)).toString(16).padStart(2, "0");
  return `#${channel(c)}${channel(m)}${channel(y)}`;
}

/** Hex-Wert mit Herkunft für Bericht und Export: „#2874b2 (ungefähr, aus RAL 5015 Himmelblau)“. */
export function describeBrandColor(hex: string | undefined, ral: string | undefined, cmyk: string | undefined): string {
  if (!hex) return "";
  if (ral) return `${hex} (ungefähr, aus ${ral})`;
  if (cmyk) return `${hex} (ungefähr, aus CMYK ${cmyk})`;
  return hex;
}
